<template>
  <div class="user-profile">
    <div class="page-header">
      <h2>个人中心</h2>
    </div>
    
    <el-row :gutter="20">
      <el-col :span="12">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>基本信息</span>
          </div>
          <el-form :model="profileForm" ref="profileForm" :rules="profileRules" label-width="100px" v-loading="loading">
            <el-form-item label="用户名">
              <el-input v-model="profileForm.username" disabled></el-input>
            </el-form-item>
            <el-form-item label="角色">
              <el-input v-model="profileForm.roleDisplay" disabled></el-input>
            </el-form-item>
            <el-form-item label="姓名" prop="first_name">
              <el-input v-model="profileForm.first_name"></el-input>
            </el-form-item>
            <!-- 姓氏在中文语境下通常和名字合并，这里根据后端模型是分开的，如果需要可以都显示，或者只显示 first_name 作为姓名 -->
            <!-- 假设 first_name 用作全名，或者我们可以显示 last_name -->
             <el-form-item label="姓氏" prop="last_name">
              <el-input v-model="profileForm.last_name"></el-input>
            </el-form-item>
            <el-form-item label="邮箱" prop="email">
              <el-input v-model="profileForm.email"></el-input>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleUpdateProfile" :loading="saving">保存修改</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
      
      <el-col :span="12">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>安全设置</span>
          </div>
          <el-form :model="passwordForm" ref="passwordForm" :rules="passwordRules" label-width="100px">
            <el-form-item label="当前密码" prop="old_password">
              <el-input type="password" v-model="passwordForm.old_password" show-password></el-input>
            </el-form-item>
            <el-form-item label="新密码" prop="new_password">
              <el-input type="password" v-model="passwordForm.new_password" show-password></el-input>
            </el-form-item>
            <el-form-item label="确认新密码" prop="confirm_password">
              <el-input type="password" v-model="passwordForm.confirm_password" show-password></el-input>
            </el-form-item>
            <el-form-item>
              <el-button type="danger" @click="handleChangePassword" :loading="changingPassword">修改密码</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" style="margin-top: 20px;">
      <el-col :span="24">
        <el-card class="box-card">
          <div slot="header" class="clearfix">
            <span>AI 用量</span>
          </div>
          <div v-loading="aiUsageLoading">
            <div v-if="aiUsage && !aiUsage.ai_available" style="margin-bottom: 12px; color: #f56c6c;">
              当前 AI 功能不可用：{{ aiUsage.blocked_reason }}
            </div>
            <el-row :gutter="20">
              <el-col :span="8">
                <div>本月 Tokens：{{ aiUsage?.month_total_tokens || 0 }} / {{ aiUsage?.limits?.monthly_tokens || '-' }}</div>
              </el-col>
              <el-col :span="8">
                <div>今日来访者：{{ aiUsage?.today_requests?.visitor || 0 }}（剩余 {{ aiUsage?.today_remaining?.visitor || 0 }}）</div>
              </el-col>
              <el-col :span="8">
                <div>今日咨询师：{{ aiUsage?.today_requests?.clinical || 0 }}（剩余 {{ aiUsage?.today_remaining?.clinical || 0 }}）</div>
              </el-col>
            </el-row>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getMe, updateMe, changePassword } from '@/api/auth'
import { getMyAiUsageSummary } from '@/api/assessments'
import { mapState } from 'vuex'
import { notifyApiError } from '@/utils/notifyApiError'
import { toastError } from '@/utils/toast'

export default {
  name: 'UserProfile',
  data() {
    var validatePass2 = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请再次输入密码'));
      } else if (value !== this.passwordForm.new_password) {
        callback(new Error('两次输入密码不一致!'));
      } else {
        callback();
      }
    };
    return {
      loading: false,
      saving: false,
      changingPassword: false,
      aiUsageLoading: false,
      aiUsage: null,
      profileForm: {
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        roleDisplay: ''
      },
      profileRules: {
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ]
      },
      passwordForm: {
        old_password: '',
        new_password: '',
        confirm_password: ''
      },
      passwordRules: {
        old_password: [
          { required: true, message: '请输入当前密码', trigger: 'blur' }
        ],
        new_password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于 6 个字符', trigger: 'blur' }
        ],
        confirm_password: [
          { required: true, validator: validatePass2, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState(['me'])
  },
  created() {
    this.fetchProfile()
    this.fetchAiUsage()
  },
  methods: {
    async fetchProfile() {
      this.loading = true
      try {
        const res = await getMe()
        const data = res.data
        this.profileForm = {
          username: data.username,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          roleDisplay: this.getRoleDisplay(data)
        }
      } catch (error) {
        notifyApiError(error, '获取个人信息失败')
      } finally {
        this.loading = false
      }
    },
    async fetchAiUsage() {
      this.aiUsageLoading = true
      try {
        const res = await getMyAiUsageSummary()
        this.aiUsage = res.data
      } catch (e) {
        this.aiUsage = null
      } finally {
        this.aiUsageLoading = false
      }
    },
    getRoleDisplay(user) {
      if (user.is_superuser) return '超级管理员'
      if (user.role === 'admin') return '管理员'
      if (user.role === 'counselor') return '心理咨询师'
      return '普通用户'
    },
    handleUpdateProfile() {
      this.$refs.profileForm.validate(async (valid) => {
        if (valid) {
          this.saving = true
          try {
            const payload = {
              first_name: this.profileForm.first_name,
              last_name: this.profileForm.last_name,
              email: this.profileForm.email
            }
            await updateMe(payload)
            this.$message.success('个人信息保存成功')
            // 更新 Vuex 中的 me
            this.$store.dispatch('fetchMe')
          } catch (error) {
            console.error(error)
            notifyApiError(error, '保存失败')
          } finally {
            this.saving = false
          }
        }
      })
    },
    handleChangePassword() {
      this.$refs.passwordForm.validate(async (valid) => {
        if (valid) {
          this.changingPassword = true
          try {
            await changePassword(this.passwordForm)
            this.$message.success('密码修改成功')
            this.$refs.passwordForm.resetFields()
          } catch (error) {
            console.error(error)
            if (error.response && error.response.data && error.response.data.old_password) {
              toastError(error.response.data.old_password[0])
            } else if (error.response && error.response.data && error.response.data.non_field_errors) {
              toastError(error.response.data.non_field_errors[0])
            } else {
              notifyApiError(error, '密码修改失败，请重试')
            }
          } finally {
            this.changingPassword = false
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.user-profile {
  padding: 20px;
}
.page-header {
  margin-bottom: 20px;
}
</style>
